import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEmployee } from 'context/EmployeeContext';
import { getEmployeeProfile, updateEmployeeProfile, getWorkplaces } from 'services/employeePortal';
import { getDepartments } from 'services/departments';
import { createNotification } from 'services/timeOff';
import AddressAutocomplete from 'components/AddressAutocomplete';

const companyId = () => localStorage.getItem('companyId');

// Auto-derive localTaxArea from city + state so it flows correctly into payroll
const deriveLocalTaxArea = (city, state) => {
    if (!city || !state) return null;
    const c = city.trim().toUpperCase();
    const s = state.trim().toUpperCase();

    if (s === 'NY') {
        const nycCities = [
            'NEW YORK', 'MANHATTAN', 'BROOKLYN', 'QUEENS', 'BRONX', 'STATEN ISLAND',
            'ASTORIA', 'FLUSHING', 'JAMAICA', 'BAYSIDE', 'FOREST HILLS', 'JACKSON HEIGHTS',
            'CORONA', 'ELMHURST', 'RIDGEWOOD', 'MASPETH', 'RICHMOND HILL', 'WOODSIDE',
            'LONG ISLAND CITY', 'SUNNYSIDE', 'HARLEM', 'INWOOD', 'WASHINGTON HEIGHTS',
        ];
        if (nycCities.some(n => c === n || c.startsWith(n))) return 'NYC';
        return null;
    }

    if (s === 'OH') {
        if (c.includes('CINCINNATI')) return 'CINCINNATI';
        if (c.includes('COLUMBUS'))   return 'COLUMBUS';
        if (c.includes('CLEVELAND'))  return 'CLEVELAND';
        if (c.includes('TOLEDO'))     return 'TOLEDO';
        if (c.includes('AKRON'))      return 'AKRON';
        return null;
    }

    if (s === 'MD') {
        const mdMap = [
            { code: 'MONTGOMERY',     cities: ['ROCKVILLE', 'GAITHERSBURG', 'BETHESDA', 'SILVER SPRING', 'CHEVY CHASE', 'GERMANTOWN', 'POTOMAC', 'OLNEY', 'CLARKSBURG', 'DAMASCUS', 'KENSINGTON', 'TAKOMA PARK', 'WHEATON', 'ASPEN HILL', 'NORTH BETHESDA', 'MONTGOMERY VILLAGE', 'POOLESVILLE'] },
            { code: 'PRINCE GEORGES', cities: ['COLLEGE PARK', 'HYATTSVILLE', 'BOWIE', 'LAUREL', 'GREENBELT', 'BLADENSBURG', 'LANDOVER', 'UPPER MARLBORO', 'SEAT PLEASANT', 'MOUNT RAINIER', 'CAPITOL HEIGHTS', 'DISTRICT HEIGHTS', 'OXON HILL', 'TEMPLE HILLS', 'SUITLAND', 'RIVERDALE', 'NEW CARROLLTON', 'CHEVERLY', 'LANHAM', 'LARGO', 'CLINTON'] },
            { code: 'BALTIMORE CITY',  cities: ['BALTIMORE'] },
            { code: 'BALTIMORE COUNTY', cities: ['TOWSON', 'CATONSVILLE', 'ESSEX', 'DUNDALK', 'PARKVILLE', 'PIKESVILLE', 'REISTERSTOWN', 'OWINGS MILLS', 'RANDALLSTOWN', 'ROSEDALE', 'MIDDLE RIVER', 'WHITE MARSH', 'PERRY HALL', 'TIMONIUM', 'LUTHERVILLE', 'COCKEYSVILLE', 'HUNT VALLEY'] },
            { code: 'HOWARD',          cities: ['COLUMBIA', 'ELLICOTT CITY', 'ELKRIDGE', 'JESSUP', 'SAVAGE', 'FULTON', 'CLARKSVILLE', 'HIGHLAND'] },
            { code: 'ANNE ARUNDEL',    cities: ['ANNAPOLIS', 'GLEN BURNIE', 'PASADENA', 'SEVERN', 'SEVERNA PARK', 'ARNOLD', 'CROFTON', 'ODENTON', 'MILLERSVILLE', 'EDGEWATER'] },
            { code: 'FREDERICK',       cities: ['FREDERICK', 'URBANA', 'WALKERSVILLE', 'THURMONT', 'EMMITSBURG', 'BRUNSWICK', 'MIDDLETOWN'] },
            { code: 'CARROLL',         cities: ['WESTMINSTER', 'ELDERSBURG', 'SYKESVILLE', 'TANEYTOWN', 'MOUNT AIRY', 'MANCHESTER', 'HAMPSTEAD'] },
            { code: 'HARFORD',         cities: ['BEL AIR', 'ABERDEEN', 'HAVRE DE GRACE', 'FALLSTON', 'FOREST HILL', 'EDGEWOOD', 'JOPPA'] },
            { code: 'CHARLES',         cities: ['LA PLATA', 'WALDORF', 'WHITE PLAINS', 'INDIAN HEAD', 'BRYANS ROAD'] },
        ];
        for (const { code, cities } of mdMap) {
            if (cities.some(city => c === city || c.startsWith(city))) return code;
        }
        return null;
    }

    return null;
};

const notifyCompany = (title, message, type, link) => {
    const cid = companyId();
    if (!cid) return;
    createNotification({ recipientId: cid, recipientType: 'company', title, message, type, link }).catch(() => {});
};

const StatusBadge = ({ label, color }) => (
    <span style={{ backgroundColor: color, color: '#fff', padding: '4px 14px', borderRadius: '20px', fontSize: 13, fontWeight: 500 }}>
        {label}
    </span>
);

const InfoField = ({ label, value }) => (
    <div style={{ marginBottom: 20 }}>
        <p style={{ color: '#6c757d', fontSize: 13, marginBottom: 4 }}>{label}</p>
        <p style={{ fontWeight: 500, fontSize: 14, margin: 0 }}>{value || '–'}</p>
    </div>
);

const EditField = ({ label, name, value, onChange, type = 'text', readOnly = false }) => (
    <div style={{ marginBottom: 20 }}>
        <label style={{ color: '#6c757d', fontSize: 13, marginBottom: 4, display: 'block' }}>{label}</label>
        {readOnly ? (
            <p style={{ fontWeight: 500, fontSize: 14, margin: 0 }}>{value || '–'}</p>
        ) : (
            <input
                type={type}
                name={name}
                value={value || ''}
                onChange={onChange}
                style={{
                    width: '100%', border: '1px solid #dee2e6', borderRadius: 6,
                    padding: '7px 10px', fontSize: 14, outline: 'none',
                    background: '#fff', color: '#333',
                }}
            />
        )}
    </div>
);

const Section = ({ title, children, action }) => (
    <div style={{ background: '#fff', borderRadius: 10, padding: '24px', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h5 style={{ color: '#1a9fbb', fontWeight: 700, margin: 0 }}>{title}</h5>
            {action}
        </div>
        {children}
    </div>
);

const formatDate = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' }); }
    catch { return iso; }
};

const maskSSN = (ssn) => {
    if (!ssn) return '–';
    const clean = ssn.replace(/\D/g, '');
    return `••••••••${clean.slice(-4)}`;
};

const FEDERAL_FILING_STATUSES = [
    { value: 'S',   label: 'Single or Married Filing Separately' },
    { value: 'MFJ', label: 'Married Filing Jointly or Qualifying Surviving Spouse' },
    { value: 'HOH', label: 'Head of Household' },
];

const STATE_FILING_STATUSES = [
    { value: 'S',   label: 'Single' },
    { value: 'MFJ', label: 'Married Filing Jointly' },
    { value: 'HOH', label: 'Head of Household' },
];

const ManageEmployeeProfile = () => {
    const { employeeId } = useEmployee();
    const queryClient = useQueryClient();
    const [editContact, setEditContact] = useState(false);
    const [contactForm, setContactForm] = useState({});
    const [successMsg, setSuccessMsg] = useState('');
    const [editW4, setEditW4] = useState(false);
    const [w4Form, setW4Form] = useState({});
    const [w4Msg, setW4Msg] = useState('');
    const [w4ErrMsg, setW4ErrMsg] = useState('');

    const { data: employee, isLoading } = useQuery({
        queryKey: ['ep-employee', employeeId],
        queryFn: () => getEmployeeProfile(employeeId),
        enabled: !!employeeId,
    });

    const { data: workplacesList = [] } = useQuery({ queryKey: ['ep-workplaces'], queryFn: getWorkplaces });
    const { data: departmentsList = [] } = useQuery({ queryKey: ['ep-departments'], queryFn: getDepartments });

    const workplaceName = useMemo(() => {
        if (!employee?.workplaceId && !employee?.primaryWorkplaceId) return null;
        const id = employee.workplaceId || employee.primaryWorkplaceId;
        return workplacesList.find(w => w.id === id)?.name || null;
    }, [employee, workplacesList]);

    const departmentName = useMemo(() => {
        if (!employee?.departmentId) return null;
        return departmentsList.find(d => d.id === employee.departmentId)?.name || null;
    }, [employee, departmentsList]);

    useEffect(() => {
        if (employee) {
            setContactForm({
                addressLine1: employee.addressLine1 || '',
                addressLine2: employee.addressLine2 || '',
                city: employee.city || '',
                state: employee.state || '',
                zip: employee.zip || '',
                email: employee.email || '',
                phone: employee.phone || '',
                cellPhone: employee.cellPhone || '',
                homePhone: employee.homePhone || '',
            });
            setW4Form({
                federalFilingStatus:      employee.federalFilingStatus   || employee.filingStatus || 'S',
                stateFilingStatus:        employee.stateFilingStatus     || 'S',
                federalExtraWithholding:  employee.federalExtraWithholding ?? employee.extraWithholding ?? '',
                multipleJobs:             ['true', true, 'Yes', 'yes', '1', 1].includes(employee.multipleJobs) ? 'true' : 'false',
                dependentsAmount:         employee.dependentsAmount       ?? employee.claimDependents ?? '',
                otherIncome:              employee.otherIncome            ?? '',
                deductionsAmount:         employee.deductionsAmount       ?? '',
            });
        }
    }, [employee]);

    const { mutate: saveContact, isPending: saving } = useMutation({
        mutationFn: (payload) => updateEmployeeProfile(employeeId, payload),
        onSuccess: () => {
            queryClient.invalidateQueries(['ep-employee', employeeId]);
            setEditContact(false);
            setSuccessMsg('Profile updated successfully.');
            setTimeout(() => setSuccessMsg(''), 3000);
            const name = [employee?.firstName, employee?.lastName].filter(Boolean).join(' ') || 'An employee';
            notifyCompany(
                'Employee Profile Updated',
                `${name} updated their contact information.`,
                'profile_update',
                `/employees/view?code=${employeeId}`
            );
        },
    });

    const { mutate: saveW4, isPending: savingW4 } = useMutation({
        mutationFn: (payload) => updateEmployeeProfile(employeeId, payload),
        onSuccess: () => {
            queryClient.invalidateQueries(['ep-employee', employeeId]);
            setEditW4(false);
            setW4ErrMsg('');
            setW4Msg('W-4 withholding updated successfully. Changes take effect on the next payroll.');
            setTimeout(() => setW4Msg(''), 5000);
            const name = [employee?.firstName, employee?.lastName].filter(Boolean).join(' ') || 'An employee';
            notifyCompany(
                'W-4 Withholding Updated',
                `${name} updated their W-4 tax withholding. Review before next payroll.`,
                'w4_update',
                `/employees/view?code=${employeeId}`
            );
        },
        onError: (err) => {
            const apiMsg = err?.response?.data?.message
                || err?.response?.data?.title
                || (typeof err?.response?.data === 'string' ? err.response.data : null)
                || 'Failed to save W-4. Please try again or contact your payroll administrator.';
            setW4ErrMsg(apiMsg);
            setTimeout(() => setW4ErrMsg(''), 8000);
        },
    });

    const handleW4Change = (e) => {
        const { name, value, type, checked } = e.target;
        setW4Form(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleW4Save = () => {
        // Field names must exactly match UpdateEmployeeRequestDto properties (case-insensitive for JSON)
        saveW4({
            federalFilingStatus:     w4Form.federalFilingStatus     || 'S',
            stateFilingStatus:       w4Form.stateFilingStatus       || 'S',
            federalExtraWithholding: w4Form.federalExtraWithholding !== '' ? Number(w4Form.federalExtraWithholding) : 0,
            multipleJobs:            w4Form.multipleJobs === true || w4Form.multipleJobs === 'true' ? 'true' : 'false',
            dependentsAmount:        w4Form.dependentsAmount !== '' ? Number(w4Form.dependentsAmount) : 0,
            otherIncome:             w4Form.otherIncome !== '' ? Number(w4Form.otherIncome) : 0,
            deductionsAmount:        w4Form.deductionsAmount !== '' ? Number(w4Form.deductionsAmount) : 0,
        });
    };

    const handleContactChange = (e) => {
        setContactForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleContactSave = () => {
        const localTaxArea = deriveLocalTaxArea(contactForm.city, contactForm.state);
        saveContact({ ...contactForm, localTaxArea });
    };

    const activeLabel = employee?.active ? 'Active' : 'Inactive';
    const activeColor = employee?.active ? '#28a745' : '#6c757d';
    const onboardStatusRaw = (employee?.onboardStatus || employee?.status || 'pending').toLowerCase();
    const onboardLabel = onboardStatusRaw === 'completed' ? 'Completed' : onboardStatusRaw === 'active' ? 'Completed' : 'N/A';
    const onboardColor = (onboardStatusRaw === 'completed' || onboardStatusRaw === 'active') ? '#28a745' : '#6c757d';

    if (!employeeId) {
        return <div style={{ padding: 24, textAlign: 'center', color: '#6c757d' }}>No employee session found. Please log in again.</div>;
    }

    if (isLoading) {
        return <div style={{ padding: 24, color: '#6c757d' }}>Loading profile...</div>;
    }

    return (
        <div style={{ padding: '24px', backgroundColor: '#F8F9FA', minHeight: '100vh' }}>
            <h2 style={{ color: '#1a9fbb', fontWeight: 700, marginBottom: 4 }}>Manage Employee Profile</h2>
            <p style={{ color: '#6c757d', marginBottom: 24 }}>View your profile information and update your contact details.</p>

            {successMsg && (
                <div style={{ background: '#d4edda', color: '#155724', border: '1px solid #c3e6cb', borderRadius: 8, padding: '10px 16px', marginBottom: 20, fontSize: 14 }}>
                    ✓ {successMsg}
                </div>
            )}

            {/* Employee ID + Status */}
            <div style={{ background: '#fff', borderRadius: 10, padding: '20px 24px', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    <span style={{ fontWeight: 600, fontSize: 15 }}>Employee ID:</span>
                    <span style={{ fontSize: 13, color: '#555', fontFamily: 'monospace' }}>
                        {employeeId ? `••••${employeeId.slice(-6)}` : '—'}
                    </span>
                </div>
                <div style={{ display: 'flex', gap: 40 }}>
                    <div>
                        <p style={{ color: '#6c757d', marginBottom: 8, fontSize: 13 }}>Onboard Status</p>
                        <StatusBadge label={onboardLabel} color={onboardColor} />
                    </div>
                    <div>
                        <p style={{ color: '#6c757d', marginBottom: 8, fontSize: 13 }}>Active Status</p>
                        <StatusBadge label={activeLabel} color={activeColor} />
                    </div>
                </div>
            </div>

            {/* General Information — read-only */}
            <Section title="General Information">
                <div className="row">
                    <div className="col-md-4"><InfoField label="First Name" value={employee?.firstName} /></div>
                    <div className="col-md-4"><InfoField label="Middle Name" value={employee?.middleName} /></div>
                    <div className="col-md-4"><InfoField label="Last Name" value={employee?.lastName} /></div>
                    <div className="col-md-4"><InfoField label="Payment Method" value={
                        employee?.defaultPaymentMethod === 'direct_deposit' ? 'Direct Deposit'
                        : employee?.defaultPaymentMethod === 'check' ? 'Check'
                        : employee?.defaultPaymentMethod
                            ? employee.defaultPaymentMethod.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                            : '–'
                    } /></div>
                    <div className="col-md-4"><InfoField label="SSN" value={maskSSN(employee?.ssn)} /></div>
                    <div className="col-md-4"><InfoField label="Date of Birth" value={formatDate(employee?.dateOfBirth)} /></div>
                    <div className="col-md-4"><InfoField label="Start Date" value={formatDate(employee?.startDate)} /></div>
                    <div className="col-md-4"><InfoField label="Termination Date" value={formatDate(employee?.terminationDate)} /></div>
                </div>
            </Section>

            {/* Contact Information — editable */}
            <Section
                title="Contact Information"
                action={
                    !editContact ? (
                        <button
                            onClick={() => setEditContact(true)}
                            style={{ background: '#E7730A', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 18px', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
                        >
                            Edit
                        </button>
                    ) : (
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button
                                onClick={handleContactSave}
                                disabled={saving}
                                style={{ background: '#28a745', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 18px', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
                            >
                                {saving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                                onClick={() => setEditContact(false)}
                                style={{ background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 18px', cursor: 'pointer', fontSize: 13 }}
                            >
                                Cancel
                            </button>
                        </div>
                    )
                }
            >
                {editContact ? (
                    <div className="row">
                        <div className="col-md-8">
                            <AddressAutocomplete
                                label="Address Line 1"
                                value={contactForm.addressLine1}
                                onSelect={(a) => setContactForm(prev => ({ ...prev, addressLine1: a.line1, city: a.city, state: a.state, zip: a.zip }))}
                                onChange={(v) => setContactForm(prev => ({ ...prev, addressLine1: v }))}
                            />
                        </div>
                        <div className="col-md-4">
                            <EditField label="Address Line 2" name="addressLine2" value={contactForm.addressLine2} onChange={handleContactChange} />
                        </div>
                        <div className="col-md-4">
                            <EditField label="City" name="city" value={contactForm.city} onChange={handleContactChange} />
                        </div>
                        <div className="col-md-4">
                            <EditField label="State" name="state" value={contactForm.state} onChange={handleContactChange} />
                        </div>
                        <div className="col-md-4">
                            <EditField label="Zip Code" name="zip" value={contactForm.zip} onChange={handleContactChange} />
                        </div>
                        <div className="col-md-4">
                            <EditField label="Email" name="email" type="email" value={contactForm.email} onChange={handleContactChange} />
                        </div>
                        <div className="col-md-4">
                            <EditField label="Cell Phone" name="cellPhone" value={contactForm.cellPhone} onChange={handleContactChange} />
                        </div>
                        <div className="col-md-4">
                            <EditField label="Home Phone" name="homePhone" value={contactForm.homePhone} onChange={handleContactChange} />
                        </div>
                    </div>
                ) : (
                    <div className="row">
                        <div className="col-md-4">
                            <InfoField label="Residence" value={[employee?.addressLine1, employee?.city, employee?.state, employee?.zip].filter(Boolean).join(', ')} />
                        </div>
                        <div className="col-md-4"><InfoField label="Email" value={employee?.email} /></div>
                        <div className="col-md-4"><InfoField label="Cell Phone" value={employee?.cellPhone} /></div>
                        <div className="col-md-4"><InfoField label="Home Phone" value={employee?.homePhone} /></div>
                    </div>
                )}
            </Section>

            {/* Workplace Information — read-only */}
            <Section title="Workplace Information">
                <div className="row">
                    <div className="col-md-4">
                        <InfoField label="Primary Workplace" value={workplaceName || employee?.workplaceId || null} />
                    </div>
                    <div className="col-md-4">
                        <InfoField label="Department" value={departmentName || employee?.departmentId || 'No Department Assigned'} />
                    </div>
                </div>
            </Section>

            {/* W-4 Federal Tax Withholding — self-service */}
            {w4Msg && (
                <div style={{ background: '#d4edda', color: '#155724', border: '1px solid #c3e6cb', borderRadius: 8, padding: '10px 16px', marginBottom: 20, fontSize: 14 }}>
                    ✓ {w4Msg}
                </div>
            )}
            {w4ErrMsg && (
                <div style={{ background: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb', borderRadius: 8, padding: '10px 16px', marginBottom: 20, fontSize: 14 }}>
                    ✗ {w4ErrMsg}
                </div>
            )}
            <Section
                title="W-4 Federal Tax Withholding"
                action={
                    !editW4 ? (
                        <button
                            onClick={() => setEditW4(true)}
                            style={{ background: '#E7730A', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 18px', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
                        >
                            Update W-4
                        </button>
                    ) : (
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button
                                onClick={handleW4Save}
                                disabled={savingW4}
                                style={{ background: '#28a745', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 18px', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
                            >
                                {savingW4 ? 'Saving...' : 'Save W-4'}
                            </button>
                            <button
                                onClick={() => setEditW4(false)}
                                style={{ background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 18px', cursor: 'pointer', fontSize: 13 }}
                            >
                                Cancel
                            </button>
                        </div>
                    )
                }
            >
                {editW4 ? (
                    <div className="row">
                        <div className="col-md-12" style={{ marginBottom: 16 }}>
                            <p style={{ fontSize: 12, color: '#6c757d', margin: 0 }}>
                                Update your federal and state withholding elections based on Form W-4 (2020 or later). Changes take effect on the next payroll run. Contact HR if you have questions.
                            </p>
                        </div>
                        <div className="col-md-6" style={{ marginBottom: 20 }}>
                            <label style={{ color: '#6c757d', fontSize: 13, marginBottom: 4, display: 'block' }}>Federal Filing Status (Step 1c)</label>
                            <select
                                name="federalFilingStatus"
                                value={w4Form.federalFilingStatus || 'S'}
                                onChange={handleW4Change}
                                style={{ width: '100%', border: '1px solid #dee2e6', borderRadius: 6, padding: '7px 10px', fontSize: 14, background: '#fff' }}
                            >
                                {FEDERAL_FILING_STATUSES.map(s => (
                                    <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-6" style={{ marginBottom: 20 }}>
                            <label style={{ color: '#6c757d', fontSize: 13, marginBottom: 4, display: 'block' }}>State Filing Status</label>
                            <select
                                name="stateFilingStatus"
                                value={w4Form.stateFilingStatus || 'S'}
                                onChange={handleW4Change}
                                style={{ width: '100%', border: '1px solid #dee2e6', borderRadius: 6, padding: '7px 10px', fontSize: 14, background: '#fff' }}
                            >
                                {STATE_FILING_STATUSES.map(s => (
                                    <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-4" style={{ marginBottom: 20 }}>
                            <label style={{ color: '#6c757d', fontSize: 13, marginBottom: 4, display: 'block' }}>Extra Withholding ($) — Step 4c</label>
                            <input
                                type="number" min="0" step="1" name="federalExtraWithholding"
                                value={w4Form.federalExtraWithholding ?? ''} onChange={handleW4Change}
                                placeholder="0"
                                style={{ width: '100%', border: '1px solid #dee2e6', borderRadius: 6, padding: '7px 10px', fontSize: 14, background: '#fff', color: '#333' }}
                            />
                        </div>
                        <div className="col-md-4" style={{ marginBottom: 20 }}>
                            <label style={{ color: '#6c757d', fontSize: 13, marginBottom: 4, display: 'block' }}>Other Income ($) — Step 4a</label>
                            <input
                                type="number" min="0" step="1" name="otherIncome"
                                value={w4Form.otherIncome ?? ''} onChange={handleW4Change}
                                placeholder="0"
                                style={{ width: '100%', border: '1px solid #dee2e6', borderRadius: 6, padding: '7px 10px', fontSize: 14, background: '#fff', color: '#333' }}
                            />
                        </div>
                        <div className="col-md-4" style={{ marginBottom: 20 }}>
                            <label style={{ color: '#6c757d', fontSize: 13, marginBottom: 4, display: 'block' }}>Deductions ($) — Step 4b</label>
                            <input
                                type="number" min="0" step="1" name="deductionsAmount"
                                value={w4Form.deductionsAmount ?? ''} onChange={handleW4Change}
                                placeholder="0"
                                style={{ width: '100%', border: '1px solid #dee2e6', borderRadius: 6, padding: '7px 10px', fontSize: 14, background: '#fff', color: '#333' }}
                            />
                        </div>
                        <div className="col-md-6" style={{ marginBottom: 20 }}>
                            <label style={{ color: '#6c757d', fontSize: 13, marginBottom: 4, display: 'block' }}>Dependent Care Amount ($) — Step 3</label>
                            <input
                                type="number" min="0" step="1" name="dependentsAmount"
                                value={w4Form.dependentsAmount ?? ''} onChange={handleW4Change}
                                placeholder="0"
                                style={{ width: '100%', border: '1px solid #dee2e6', borderRadius: 6, padding: '7px 10px', fontSize: 14, background: '#fff', color: '#333' }}
                            />
                        </div>
                        <div className="col-md-12" style={{ marginBottom: 8 }}>
                            <label style={{ cursor: 'pointer', fontSize: 13, color: '#555' }}>
                                <input
                                    type="checkbox"
                                    name="multipleJobs"
                                    checked={w4Form.multipleJobs === 'true' || w4Form.multipleJobs === true}
                                    onChange={e => setW4Form(prev => ({ ...prev, multipleJobs: e.target.checked ? 'true' : 'false' }))}
                                    style={{ marginRight: 8 }}
                                />
                                Step 2 — Multiple Jobs / Spouse also works (check if applicable)
                            </label>
                        </div>
                    </div>
                ) : (
                    <div className="row">
                        <div className="col-md-4">
                            <InfoField label="Federal Filing Status" value={FEDERAL_FILING_STATUSES.find(s => s.value === employee?.federalFilingStatus)?.label || employee?.federalFilingStatus || '—'} />
                        </div>
                        <div className="col-md-4">
                            <InfoField label="State Filing Status" value={STATE_FILING_STATUSES.find(s => s.value === employee?.stateFilingStatus)?.label || employee?.stateFilingStatus || '—'} />
                        </div>
                        <div className="col-md-4">
                            <InfoField label="Extra Withholding" value={employee?.federalExtraWithholding != null ? `$${Number(employee.federalExtraWithholding).toFixed(2)}` : '$0.00'} />
                        </div>
                        <div className="col-md-4">
                            <InfoField label="Other Income (Step 4a)" value={employee?.otherIncome != null ? `$${Number(employee.otherIncome).toFixed(2)}` : '$0.00'} />
                        </div>
                        <div className="col-md-4">
                            <InfoField label="Deductions (Step 4b)" value={employee?.deductionsAmount != null ? `$${Number(employee.deductionsAmount).toFixed(2)}` : '$0.00'} />
                        </div>
                        <div className="col-md-4">
                            <InfoField label="Multiple Jobs (Step 2)" value={['true', true, 'Yes', 'yes', '1', 1].includes(employee?.multipleJobs) ? 'Yes' : 'No'} />
                        </div>
                    </div>
                )}
            </Section>
        </div>
    );
};

export default ManageEmployeeProfile;
